import React, { useEffect, useState, useRef } from 'react';
import { Menu, Plus, ChevronDown, User, Settings, LogOut } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { NuovoInterventoFormModal } from './NuovoInterventoFormModal';
interface HeaderProps {
  onMenuClick?: () => void;
}
export function Header({
  onMenuClick
}: HeaderProps) {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Cleaner Manager context = any route under /cm; Manutentore = any route under /man
  const isCleanerManager = location.pathname.startsWith('/cm');
  const isMaintainer = location.pathname.startsWith('/man');
  const isHost = !isCleanerManager && !isMaintainer;
  const profile = isCleanerManager ? {
    avatar: 'https://cdn.magicpatterns.com/uploads/5qDLCxcppx3Xbyc5uL4GS6/Screenshot_2026-03-22_alle_16.12.18.png',
    name: 'Francesca N.',
    role: 'Cleaner Manager'
  } : isMaintainer ? {
    avatar: 'https://cdn.magicpatterns.com/uploads/fTors2Yrr32xiEFMswChLH/Installatore-E-Manutentore-Meccanico.webp',
    name: 'Andrea Costa',
    role: 'Manutentore'
  } : {
    avatar: 'https://cdn.magicpatterns.com/uploads/aDiier9xTcmPNhp9njGfUz/80.jpg',
    name: 'Massimo Giri',
    role: 'Host'
  };
  // Close the profile menu on outside click or Escape
  useEffect(() => {
    if (!isMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isMenuOpen]);
  const handleLogout = () => {
    setIsMenuOpen(false);
    navigate('/login');
  };
  return <>
      <header className="bg-white border-b border-border shadow-sm sticky top-0 z-30 h-16 flex items-center justify-between px-4 lg:px-8" data-id="element-223">
        {/* Left: Hamburger Menu (Mobile only) */}
        <div className="flex items-center gap-4" data-id="element-224">
          {onMenuClick && <button onClick={onMenuClick} className="lg:hidden p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-225">
              <Menu size={24} className="text-gray-600" data-id="element-226" />
            </button>}
        </div>

        {/* Right: CTA + Avatar */}
        <div className="flex items-center gap-3 ml-auto" data-id="element-227">
          {/* Nuovo intervento Button */}
          <button onClick={() => {
          if (isHost) {
            navigate('/richiedi-intervento', {
              state: {
                openNew: true
              }
            });
          } else {
            setIsWizardOpen(true);
          }
        }} className="flex items-center gap-1.5 h-10 px-4 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors whitespace-nowrap" data-id="element-228">
            <Plus size={16} data-id="element-229" />
            <span className="hidden sm:inline" data-id="element-230">
              {isHost ? 'Nuovo intervento' : 'Aggiungi intervento'}
            </span>
          </button>

          {/* User Profile — dropdown menu */}
          <div className="relative" ref={menuRef} data-id="element-231">
            <button onClick={() => setIsMenuOpen(v => !v)} aria-haspopup="menu" aria-expanded={isMenuOpen} aria-label="Menu profilo" className="flex items-center gap-2.5 rounded-full p-1 sm:pr-2 hover:bg-gray-100 transition-colors" data-id="element-232">
              <img src={profile.avatar} alt="Profilo" className="w-9 h-9 rounded-full border border-border object-cover flex-shrink-0" data-id="element-233" />
              <div className="text-left hidden sm:block" data-id="element-234">
                <p className="text-sm font-semibold text-navy leading-tight" data-id="element-235">
                  {profile.name}
                </p>
                <p className="text-xs text-muted-foreground leading-tight mt-0.5" data-id="element-236">
                  {profile.role}
                </p>
              </div>
              <ChevronDown size={16} className={`text-gray-400 hidden sm:block transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} data-id="element-237" />
            </button>

            {/* Dropdown */}
            {isMenuOpen && <div role="menu" className="absolute right-0 mt-2 w-56 bg-white border border-border rounded-xl shadow-lg overflow-hidden z-40 animate-in fade-in zoom-in-95 duration-150" data-id="element-238">
                {/* Header (mobile shows identity since it's hidden in trigger) */}
                <div className="px-4 py-3 border-b border-border sm:hidden" data-id="element-239">
                  <p className="text-sm font-semibold text-navy leading-tight" data-id="element-240">
                    {profile.name}
                  </p>
                  <p className="text-xs text-muted-foreground leading-tight mt-0.5" data-id="element-241">
                    {profile.role}
                  </p>
                </div>

                <div className="py-1" data-id="element-242">
                  <Link to="/impostazioni" role="menuitem" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-gray-50 transition-colors" data-id="element-243">
                    <User size={16} className="text-gray-500" data-id="element-244" />
                    Profilo
                  </Link>
                  <Link to="/impostazioni" role="menuitem" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-gray-50 transition-colors" data-id="element-245">
                    <Settings size={16} className="text-gray-500" data-id="element-246" />
                    Impostazioni
                  </Link>
                </div>

                <div className="border-t border-border py-1" data-id="element-247">
                  <button onClick={handleLogout} role="menuitem" className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left" data-id="element-248">
                    <LogOut size={16} data-id="element-249" />
                    Logout
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </header>

      {/* Nuovo intervento — form modal (singolo / multiplo + allega contratto) */}
      <NuovoInterventoFormModal isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} data-id="element-250" />
    </>;
}